const getFiniteOrder = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const moveItem = (array, fromIndex, toIndex) => {
  if (!Array.isArray(array)) return [];

  const next = [...array];
  const hasValidFrom = fromIndex >= 0 && fromIndex < next.length;
  const hasValidTo = toIndex >= 0 && toIndex < next.length;

  if (!hasValidFrom || !hasValidTo || fromIndex === toIndex) {
    return next;
  }

  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

export const buildOrderedIds = (blocks) => {
  if (!Array.isArray(blocks) || blocks.length === 0) return [];

  return blocks
    .map((block, index) => ({
      id: block?.id == null ? null : String(block.id),
      index,
      order: getFiniteOrder(block?.order, index),
    }))
    .filter((entry) => entry.id != null)
    .sort((a, b) => a.order - b.order || a.index - b.index)
    .map((entry) => entry.id);
};

export const commitBlockLibraryOrder = ({ coach, orderedIds, allowedIds }) => {
  if (!coach || !Array.isArray(orderedIds) || orderedIds.length === 0) {
    return false;
  }

  const blocks = Array.isArray(coach.blockLibrary) ? coach.blockLibrary : [];
  if (blocks.length === 0) return false;

  const blockById = new Map(
    blocks.map((block) => [String(block?.id ?? "").toLowerCase(), block]),
  );
  const hasAllowlist = Array.isArray(allowedIds);
  const allowlist = hasAllowlist
    ? new Set(
        allowedIds
          .map((id) => (id == null ? "" : String(id).toLowerCase()))
          .filter(Boolean),
      )
    : null;

  if (hasAllowlist) {
    if (!allowlist || allowlist.size === 0) return false;

    const seenAllowlistIds = new Set();
    for (const id of orderedIds) {
      const normalizedId = id == null ? "" : String(id).toLowerCase();
      if (!normalizedId || seenAllowlistIds.has(normalizedId)) continue;
      seenAllowlistIds.add(normalizedId);
      if (!allowlist.has(normalizedId)) {
        return false;
      }
    }

    if (seenAllowlistIds.size !== allowlist.size) {
      return false;
    }
  }

  const seen = new Set();
  const knownOrderedBlocks = orderedIds
    .map((id) => (id == null ? null : String(id).toLowerCase()))
    .map((normalizedId) => {
      if (!normalizedId || seen.has(normalizedId)) return null;
      if (allowlist && !allowlist.has(normalizedId)) return null;
      seen.add(normalizedId);
      return blockById.get(normalizedId) ?? null;
    })
    .filter(Boolean);

  let hasChanges = false;

  knownOrderedBlocks.forEach((block, index) => {
    const normalizedId = String(block?.id ?? "").toLowerCase();
    const currentOrder = getFiniteOrder(block?.order, -1);
    if (currentOrder === index) return;

    if (typeof block?.setProperty === "function") {
      block.setProperty("order", index);
    } else {
      block.order = index;
    }

    coach?.updateCoachTransactionBlocks?.(normalizedId);
    hasChanges = true;
  });

  if (hasChanges) {
    coach?.startOrResetBatchTimer?.();
  }

  return hasChanges;
};
